import { forwardRef, useImperativeHandle, useState } from 'react';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import BookingsTabSkeleton from '@/components/v2/molecules/admin/tabs/AdminBookingsTab/BookingsTabSkeleton';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import { useBookingsFiltered } from '@/hooks/admin/tabs/useBookingsFiltered';
import { useToast } from '@/hooks/use-toast';
import {
  Booking,
  useBookings,
  useUpdateBookingStatus,
} from '@/hooks/useBookings';
import { useOrigins } from '@/hooks/useOrigins';
import { useProperties } from '@/hooks/useProperties';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useContracts } from '@/hooks/useContracts';
import { parseDateFromDatabase } from '@/utils/dateParser';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import BookingFormModal from '../../../../../BookingFormModal';
import BookingsEmptyBanner from '../../../../molecules/admin/tabs/AdminBookingsTab/BookingsEmptyBanner';
import BookingsFilteredBanner from '../../../../molecules/admin/tabs/AdminBookingsTab/BookingsFilteredBanner';
import BookingsFilters from '../AdminBookingsTab/BookingsFilters';
import BookingsTable from '../AdminBookingsTab/BookingsTable';
import GuestFormModal from '../AdminGuestsTab/GuestFormModal';
import { Guest } from '@/hooks/useGuests';

export interface ReservationsBookingsViewRef {
  downloadCSV: () => void;
  createNew: () => void;
  hasData: boolean;
}

interface ReservationsBookingsViewProps {
  onSelectionChange?: (selection: { count: number } | null) => void;
}

const ReservationsBookingsView = forwardRef<ReservationsBookingsViewRef, ReservationsBookingsViewProps>(
  ({ onSelectionChange }, ref) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);

    const [showGuestModal, setShowGuestModal] = useState(false);
    const [newGuestName, setNewGuestName] = useState('');
    const [pendingOriginId, setPendingOriginId] = useState('');
    const [pendingGuestCallback, setPendingGuestCallback] = useState<
      ((guest: Guest) => void) | null
    >(null);

    const { data: allBookings = [] } = useBookings();
    const { data: properties = [] } = useProperties();
    const { data: origins = [] } = useOrigins();
    const { data: organizations = [] } = useOrganizations();
    const { data: contracts = [] } = useContracts();

    const updateStatusMutation = useUpdateBookingStatus();

    const {
      bookings,
      totalCount,
      totalPages,
      isLoading: isLoadingBookings,
      error: errorBookings,
      refetch: refetchBookings,
      currentPage,
      pageSize,
      handlePageChange,
      handlePageSizeChange,
      guestSearch,
      selectedProperties,
      selectedOrganizations,
      selectedOrigin,
      dateFrom,
      dateTo,
      hasActiveFilters,
      handleGuestSearchChange,
      handlePropertiesChange,
      handleOrganizationsChange,
      handleOriginChange,
      handleDateFromChange,
      handleDateToChange,
      resetFilters,
    } = useBookingsFiltered();

    const { toast } = useToast();

    const formatBookingDate = (dateString: string) => {
      const date = parseDateFromDatabase(dateString);
      if (!date) return dateString;
      return format(date, 'dd/MM/yy', { locale: es });
    };

    const handleNewBooking = () => {
      setSelectedBooking(null);
      setShowModal(true);
    };

    const handleEditBooking = (booking: Booking) => {
      setSelectedBooking(booking);
      setShowModal(true);
    };

    const handleCancelBooking = (booking: Booking) => {
      if (
        window.confirm('¿Estás seguro de que quieres cancelar esta reservación?')
      ) {
        updateStatusMutation.mutate({ id: booking.id, status: 'Cancelada' });
      }
    };

    const handleActivateBooking = (booking: Booking) => {
      updateStatusMutation.mutate({ id: booking.id, status: 'Reservada' });
    };

    const handleCopyUrl = async (bookingId: string) => {
      const url = `${window.location.origin}/${bookingId}`;
      try {
        await navigator.clipboard.writeText(url);
        toast({
          title: 'URL copiada',
          description:
            'El enlace de la reservación ha sido copiado al portapapeles',
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        toast({
          title: 'Error',
          description: 'No se pudo copiar el enlace',
          variant: 'destructive',
        });
      }
    };

    const handleDownloadCSV = () => {
      const csvHeaders = [
        'Propiedad',
        'Origen',
        'Huésped',
        'Llegada',
        'Salida',
        'Noches',
        'Pago Estimado',
        'Hospedaje Realizado',
        'Pago Realizado',
      ];

      const csvData = bookings.map((booking) => [
        booking.property_short_name,
        booking.origin_name || 'N/A',
        booking.guest_name,
        formatBookingDate(booking.check_in),
        formatBookingDate(booking.check_out),
        booking.nights.toString(),
        (booking.estimated_payout ?? 0).toString(),
        booking.stay_completed ? 'Si' : 'No',
        booking.payment_status === 'paid' ? 'Pagado' : booking.payment_status === 'partial' ? 'Pago parcial' : 'Pendiente',
      ]);

      const csvContent = [
        csvHeaders.join(','),
        ...csvData.map((row) => row.map((field) => `"${field}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `reservaciones_${new Date().toISOString().split('T')[0]}.csv`,
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: 'CSV descargado',
        description: `Se han exportado ${bookings.length} reservaciones`,
      });
    };

    useImperativeHandle(ref, () => ({
      downloadCSV: handleDownloadCSV,
      createNew: handleNewBooking,
      hasData: allBookings.length > 0,
    }));

    if (isLoadingBookings) {
      return <BookingsTabSkeleton />;
    }

    if (errorBookings) {
      return (
        <TabErrorBanner
          title="Error al cargar reservaciones"
          message="No se pudieron cargar las reservaciones. Por favor, intenta nuevamente."
          onRetry={() => refetchBookings()}
        />
      );
    }

    return (
      <div className="space-y-6">
        <BookingsFilters
          guestSearch={guestSearch}
          selectedProperties={selectedProperties}
          selectedOrganizations={selectedOrganizations}
          selectedOrigin={selectedOrigin}
          checkInDate={dateFrom}
          checkOutDate={dateTo}
          setGuestSearch={handleGuestSearchChange}
          setSelectedProperties={handlePropertiesChange}
          setSelectedOrganizations={handleOrganizationsChange}
          setSelectedOrigin={handleOriginChange}
          setCheckInDate={handleDateFromChange}
          setCheckOutDate={handleDateToChange}
          properties={properties}
          organizations={organizations}
          contracts={contracts}
          origins={origins}
          hasActiveFilters={hasActiveFilters}
          clearAllFilters={resetFilters}
        />

        {allBookings.length === 0 ? (
          <BookingsEmptyBanner onCreateBooking={handleNewBooking} />
        ) : bookings.length === 0 ? (
          <BookingsFilteredBanner onClearFilters={resetFilters} />
        ) : (
          <>
            <BookingsTable
              bookings={bookings}
              onEdit={handleEditBooking}
              onCopy={handleCopyUrl}
              onCancel={handleCancelBooking}
              onActivate={handleActivateBooking}
              isLoading={updateStatusMutation.isPending}
            />
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </>
        )}

        <BookingFormModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          booking={selectedBooking}
          onBookingCreated={refetchBookings}
          origins={origins}
          onRequestGuestCreation={(guestName, originId) => {
            setShowModal(false);
            setNewGuestName(guestName);
            setPendingOriginId(originId || '');
            setShowGuestModal(true);
            setPendingGuestCallback(() => () => {
              setShowModal(true);
            });
          }}
        />

        <GuestFormModal
          isOpen={showGuestModal}
          onClose={() => {
            setShowGuestModal(false);
            setNewGuestName('');
            setPendingOriginId('');
            setPendingGuestCallback(null);
          }}
          guest={null}
          defaultName={newGuestName}
          defaultOriginId={pendingOriginId}
          onGuestCreated={(guest) => {
            setShowGuestModal(false);
            setNewGuestName('');
            setPendingOriginId('');
            pendingGuestCallback?.(guest);
          }}
        />
      </div>
    );
  }
);

ReservationsBookingsView.displayName = 'ReservationsBookingsView';

export default ReservationsBookingsView;
